import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Save, Search, X } from 'lucide-react';
import MainService from '../../services/MainService';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

const LIST_TAKE = 10000;

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isSuccess = (response) => response === true || response === 'true' || response === 'Success';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const ORDER_COLUMNS = [
  { field: 'voucherno', title: 'Voucher No', hideable: true },
  { field: 'clientbill', title: 'Client Bill', hideable: true },
  { field: 'date', title: 'Date', hideable: true },
  { field: 'client', title: 'Client', hideable: true },
  { field: 'clientmob', title: 'Client Mob', hideable: true },
  { field: 'customer', title: 'Customer', hideable: true },
  { field: 'customermob', title: 'Customer Mob', hideable: true },
  { field: 'status', title: 'Status', hideable: true },
  { field: 'refno', title: 'Ref No', hideable: true, sortable: false, filterable: false, groupable: false },
];

const getCellValue = (row, field) => {
  switch (field) {
    case 'voucherno':
      return row.VoucherNo || '';
    case 'clientbill':
      return row.ClientBillNo || '';
    case 'date':
      return formatDate(row.DeliveryDate);
    case 'client':
      return row.Client?.Name || '';
    case 'clientmob':
      return row.Client?.MobileNo1 || '';
    case 'customer':
      return row.Customer?.Name || '';
    case 'customermob':
      return row.Customer?.MobileNo1 || '';
    case 'status':
      return row.Status?.Name || '';
    case 'refno':
      return String(row.StoreRefNo ?? '');
    default:
      return row[field] ?? '';
  }
};

const UpdateStoreRefNo = () => {
  const [statuses, setStatuses] = useState([]);
  const [rows, setRows] = useState([]);
  const [statusId, setStatusId] = useState('');
  const [appliedStatusId, setAppliedStatusId] = useState('');
  const [editedRefs, setEditedRefs] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const columns = useMemo(() => ORDER_COLUMNS, []);
  const resolveCellValue = useCallback(
    (row, field) => {
      if (field === 'refno') {
        return String(editedRefs[row.Id] ?? row.StoreRefNo ?? '');
      }
      return getCellValue(row, field);
    },
    [editedRefs]
  );
  const grid = useClientListGrid({ columns, rows, getCellValue: resolveCellValue });
  const savePayload = useMemo(
    () => rows.map((row) => ({ Id: row.Id, StoreRefNo: Number(editedRefs[row.Id] ?? row.StoreRefNo) || 0 })),
    [editedRefs, rows]
  );

  const loadStatuses = useCallback(async () => {
    try {
      const response = await MainService.StatusInDropDown();
      setStatuses(parseList(response));
    } catch (err) {
      console.error('Load statuses error:', err);
      setStatuses([]);
      setError('Failed to load statuses');
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.OrdersInRefEdit({
        StatusId: Number(appliedStatusId) || 0,
        Skip: 0,
        Take: LIST_TAKE,
      });
      const list = parseList(response);
      setRows(list);
      setEditedRefs({});
    } catch (err) {
      console.error('Load orders for ref edit error:', err);
      setRows([]);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  }, [appliedStatusId]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const filterData = () => {
    setAppliedStatusId(statusId);
  };

  const refreshData = () => {
    setStatusId('');
    setAppliedStatusId('');
    if (!appliedStatusId) {
      loadRows();
    }
  };

  const updateRefDraft = (orderId, value) => {
    setEditedRefs((prev) => ({ ...prev, [orderId]: value }));
  };

  const saveStoreRefNoList = async () => {
    if (rows.length === 0) {
      setError('No orders to save');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const response = await MainService.SaveStoreRefList(savePayload);
      if (isSuccess(response)) {
        setSuccess('Success');
        await loadRows();
      } else {
        setError('Network Error');
      }
    } catch (err) {
      console.error('Save store ref list error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Update Store Ref No</h1>
            <p className="mt-1 text-sm text-gray-500">Filter orders by status and update store reference numbers.</p>
          </div>
          <button
            type="button"
            onClick={refreshData}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={statusId}
              onChange={(event) => setStatusId(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">All Statuses</option>
              {statuses.map((status) => (
                <option key={status.Id} value={status.Id}>
                  {status.Name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 lg:col-span-8">
            <button
              type="button"
              onClick={filterData}
              disabled={loading || saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              Filter
            </button>
            <button
              type="button"
              onClick={saveStoreRefNoList}
              disabled={saving || rows.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Save Ref No
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Orders</h2>
            <p className="text-sm text-gray-500">
              {grid.totalCount} orders
            </p>
          </div>
        </div>

        <ClientListGrid
          columns={columns}
          grid={grid}
          loading={loading}
          loadingText="Loading orders..."
          emptyText="No orders found."
          minWidth="1200px"
          compact
          rowKey={(row) => row.Id}
          renderCell={(row, column) => {
            if (column.field === 'refno') {
              return (
                <input
                  type="number"
                  value={editedRefs[row.Id] ?? row.StoreRefNo ?? 0}
                  onChange={(event) => updateRefDraft(row.Id, event.target.value)}
                  className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              );
            }
            if (column.field === 'voucherno') {
              return <span className="font-semibold text-gray-900">{row.VoucherNo || '-'}</span>;
            }
            return resolveCellValue(row, column.field) || '-';
          }}
        />
      </div>
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default UpdateStoreRefNo;
